import React, { useState, useEffect, useMemo } from 'react';
import PrizeNavigation from '../components/PrizeNavigation';
import styles from './DraftResults.module.css';
import { useLeague } from '../utils/LeagueContext';
import { useUser } from '../UserContext';
import useLeagueAccessGuard from '../hooks/useLeagueAccessGuard';
import API_BASE_URL from '../utils/apiConfig';

const POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'D/ST', 'DEF', 'FLEX'];

const DraftResults = () => {
  useLeagueAccessGuard();
  const { selectedLeague, selectedSeason, setSelectedSeason } = useLeague();
  const { user } = useUser();

  const leagueId = selectedLeague?._id;
  const userRole = selectedLeague?.members?.find(
    m => m.userId === user?._id
  )?.role;
  const isCommissioner = userRole === 'commissioner';

  // Data state
  const [draft, setDraft] = useState(null);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Import flow state
  const [step, setStep] = useState('idle'); // 'idle' | 'paste' | 'review'
  const [draftType, setDraftType] = useState('auction');
  const [pasteText, setPasteText] = useState('');
  const [parsedPicks, setParsedPicks] = useState([]);
  const [teamMappings, setTeamMappings] = useState({}); // { espnName: { memberId, memberName } }
  const [pickOverrides, setPickOverrides] = useState({}); // { pickIndex: { memberId, memberName } } — for picks missing teamName
  const [nameOverrides, setNameOverrides] = useState({});  // { pickIndex: string } — for picks missing playerName
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState(null);

  // Table state — sortColumns is an ordered list (max 2) of { field, dir }
  const [sortColumns, setSortColumns] = useState([{ field: 'auctionPrice', dir: 'desc' }]);
  const [filterPosition, setFilterPosition] = useState('ALL');
  const [showSortHint, setShowSortHint] = useState(
    () => !localStorage.getItem('draftSortHintDismissed')
  );

  // Edit state (commissioner only)
  const [editingPickId, setEditingPickId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Available seasons — from fee years + current year
  const availableSeasons = useMemo(() => {
    const years = new Set(
      (selectedLeague?.fees || []).map(f => f.year).filter(Boolean)
    );
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [selectedLeague]);

  // League members for team mapping dropdowns
  const leagueMembers = useMemo(() => {
    return (selectedLeague?.members || []).filter(
      m => m.inviteStatus === 'active' || !m.inviteStatus
    );
  }, [selectedLeague]);

  // Existing ESPN team mappings from league (for pre-filling the mapping step)
  const existingMappings = useMemo(() => {
    const map = {};
    (selectedLeague?.espnTeamMappings || []).forEach(m => {
      map[m.espnName] = { memberId: m.memberId, memberName: m.memberName };
    });
    return map;
  }, [selectedLeague]);

  useEffect(() => {
    if (!leagueId || !selectedSeason) return;
    fetchDraft();
  }, [leagueId, selectedSeason]);

  const fetchDraft = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/leagues/${leagueId}/draft?season=${selectedSeason}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load draft results');
      const data = await res.json();
      setDraft(data.draft);
      setPicks(data.picks || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Import flow ---

  const handleStartImport = () => {
    setPasteText('');
    setParsedPicks([]);
    setTeamMappings({});
    setPickOverrides({});
    setNameOverrides({});
    setBulkAssignMemberId('');
    setParseError(null);
    setStep('paste');
  };

  const handleParse = async () => {
    if (!pasteText.trim()) return;
    setParsing(true);
    setParseError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}/draft/parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: pasteText, draftType }),
      });
      const data = await res.json();
      // res.ok covers pre-stream errors (non-200 status).
      // data.error covers post-stream errors: once keepalive bytes are written the
      // status is committed to 200, so stream-phase failures are signalled in the body.
      if (!res.ok || data.error) throw new Error(data.message || 'Parse failed');

      setParsedPicks(data.picks);

      // Pre-fill team mappings from existing saved mappings
      const initialMappings = {};
      const initialOverrides = {};
      data.picks.forEach((p, i) => {
        if (p.teamName) {
          if (!initialMappings[p.teamName]) {
            const saved = existingMappings[p.teamName];
            initialMappings[p.teamName] = saved || { memberId: '', memberName: '' };
          }
        } else {
          // Pick has no teamName — needs a per-pick override
          initialOverrides[i] = { memberId: '', memberName: '' };
        }
      });
      setTeamMappings(initialMappings);
      setPickOverrides(initialOverrides);
      setStep('review');
    } catch (err) {
      setParseError(err.message);
    } finally {
      setParsing(false);
    }
  };

  const handleMappingChange = (espnName, memberId) => {
    const member = leagueMembers.find(m => m.userId === memberId || m._id === memberId);
    setTeamMappings(prev => ({
      ...prev,
      [espnName]: { memberId, memberName: member?.name || memberId },
    }));
  };

  const handlePickOverrideChange = (pickIndex, memberId) => {
    const member = leagueMembers.find(m => m.userId === memberId || m._id === memberId);
    setPickOverrides(prev => ({
      ...prev,
      [pickIndex]: { memberId, memberName: member?.name || '' },
    }));
  };

  const [bulkAssignMemberId, setBulkAssignMemberId] = useState('');

  const handleBulkAssign = () => {
    if (!bulkAssignMemberId) return;
    const member = leagueMembers.find(m => m.userId === bulkAssignMemberId || m._id === bulkAssignMemberId);
    setPickOverrides(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(idx => {
        // Only overwrite slots that are currently unassigned
        if (!updated[idx].memberId) {
          updated[idx] = { memberId: bulkAssignMemberId, memberName: member?.name || '' };
        }
      });
      return updated;
    });
    setBulkAssignMemberId('');
  };

  // A pick is still missing its name if it has no playerName AND no nameOverride has been entered.
  const picksWithMissingName = parsedPicks.filter((p, i) => !p.playerName && !nameOverrides[i]?.trim());

  const canImport = () => {
    if (picksWithMissingName.length > 0) return false;
    const mappingKeys = Object.keys(teamMappings);
    const overrideKeys = Object.keys(pickOverrides);
    // Must have at least one assignment mechanism, and every slot must be filled
    if (mappingKeys.length === 0 && overrideKeys.length === 0) return false;
    const mappingsComplete = Object.values(teamMappings).every(m => m.memberId);
    const overridesComplete = Object.values(pickOverrides).every(m => m.memberId);
    return mappingsComplete && overridesComplete;
  };

  const handleImport = async () => {
    if (!canImport()) {
      alert('Please resolve all flagged picks before importing.');
      return;
    }
    setImporting(true);
    try {
      // Build picks with all overrides applied.
      // playerName: use nameOverrides[i] if the parsed pick had none.
      // member assignment: picks with a teamName use the team-level mapping; picks without use their per-pick override.
      const enrichedPicks = parsedPicks.map((p, i) => {
        const mapping = p.teamName
          ? (teamMappings[p.teamName] || {})
          : (pickOverrides[i] || {});
        return {
          ...p,
          playerName: p.playerName || nameOverrides[i] || '',
          memberId: mapping.memberId || null,
          memberName: mapping.memberName || p.teamName || '',
        };
      });

      const teamMappingsArray = Object.entries(teamMappings).map(([espnName, m]) => ({
        espnName,
        memberId: m.memberId,
        memberName: m.memberName,
      }));

      const res = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}/draft/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          draftType,
          season: selectedSeason,
          picks: enrichedPicks,
          teamMappings: teamMappingsArray,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Import failed');
      setDraft(data.draft);
      setPicks(data.picks);
      setStep('idle');
      setPasteText('');
      setParsedPicks([]);
    } catch (err) {
      alert('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!draft) return;
    if (!window.confirm('Delete this draft and all picks? This cannot be undone.')) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/leagues/${leagueId}/draft/${draft._id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('Delete failed');
      setDraft(null);
      setPicks([]);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  // --- Edit pick ---

  const handleEditPick = (pick) => {
    setEditingPickId(pick._id);
    setEditForm({
      playerName: pick.playerName,
      position: pick.position || '',
      nflTeam: pick.nflTeam || '',
      memberId: pick.memberId || '',
      memberName: pick.memberName || '',
      keeper: pick.keeper || false,
      auctionPrice: pick.auctionPrice ?? '',
      pickNumber: pick.pickNumber ?? '',
      round: pick.round ?? '',
    });
  };

  const handleSavePick = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/leagues/${leagueId}/draft/picks/${editingPickId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...editForm,
            auctionPrice: editForm.auctionPrice !== '' ? Number(editForm.auctionPrice) : null,
            pickNumber: editForm.pickNumber !== '' ? Number(editForm.pickNumber) : null,
            round: editForm.round !== '' ? Number(editForm.round) : null,
          }),
        }
      );
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setPicks(prev => prev.map(p => p._id === updated._id ? updated : p));
      setEditingPickId(null);
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  // --- Table sorting & filtering ---

  const handleSort = (field, e) => {
    const isShift = e?.shiftKey;
    setSortColumns(prev => {
      const existingIdx = prev.findIndex(s => s.field === field);
      if (!isShift) {
        // Regular click: set as sole primary sort, toggle dir if already primary
        const currentDir = existingIdx === 0 ? prev[0].dir : null;
        const nextDir = currentDir === 'asc' ? 'desc' : currentDir === 'desc' ? 'asc' : 'desc';
        return [{ field, dir: nextDir }];
      }
      // Shift+click: toggle dir if already present, otherwise add/replace secondary slot
      if (existingIdx !== -1) {
        const updated = [...prev];
        updated[existingIdx] = { field, dir: prev[existingIdx].dir === 'asc' ? 'desc' : 'asc' };
        return updated;
      }
      // Add as secondary (slot 1), capped at 2
      return [prev[0], { field, dir: 'asc' }].filter(Boolean).slice(0, 2);
    });
  };

  const dismissSortHint = () => {
    setShowSortHint(false);
    localStorage.setItem('draftSortHintDismissed', '1');
  };

  const sortIcon = (field) => {
    const idx = sortColumns.findIndex(s => s.field === field);
    if (idx === -1) return <span className={styles.sortArrow}> ↕</span>;
    const arrow = sortColumns[idx].dir === 'asc' ? '↑' : '↓';
    const badge = sortColumns.length > 1 ? <sup className={styles.sortBadge}>{idx + 1}</sup> : null;
    return <span className={styles.sortArrow}> {arrow}{badge}</span>;
  };

  const displayedPicks = useMemo(() => {
    let filtered = filterPosition === 'ALL'
      ? picks
      : picks.filter(p => (p.position || '').toUpperCase() === filterPosition);

    return [...filtered].sort((a, b) => {
      for (const { field, dir } of sortColumns) {
        let av = a[field] ?? (dir === 'asc' ? Infinity : -Infinity);
        let bv = b[field] ?? (dir === 'asc' ? Infinity : -Infinity);
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return dir === 'asc' ? -1 : 1;
        if (av > bv) return dir === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [picks, filterPosition, sortColumns]);

  const uniquePositions = useMemo(() => {
    const found = Array.from(new Set(picks.map(p => (p.position || '').toUpperCase()).filter(Boolean)));
    return ['ALL', ...found.sort()];
  }, [picks]);

  // --- Render ---

  if (loading) {
    return (
      <div className={styles.page}>
        <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={leagueId} />
        <div className={styles.content}><p>Loading...</p></div>
      </div>
    );
  }

  const isAuction = draft?.draftType === 'auction' || draftType === 'auction';

  return (
    <div className={styles.page}>
      <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={leagueId} />
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.leagueName}>{selectedLeague?.leagueName}</h2>
          <h3 className={styles.title}>Draft Results</h3>
        </div>

        {/* Season picker */}
        <div className={styles.seasonRow}>
          <select
            className={styles.seasonSelect}
            value={selectedSeason}
            onChange={e => setSelectedSeason(Number(e.target.value))}
          >
            {availableSeasons.map(y => (
              <option key={y} value={y}>{y} Season</option>
            ))}
          </select>
          {draft && isCommissioner && (
            <button className={styles.deleteDraftButton} onClick={handleDeleteDraft}>
              Delete Draft
            </button>
          )}
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {/* ── Paste step ── */}
        {step === 'paste' && (
          <div className={styles.importBox}>
            <h4 className={styles.importTitle}>Import Draft Results — {selectedSeason}</h4>

            <div className={styles.field}>
              <label className={styles.label}>Draft type</label>
              <select
                className={styles.select}
                value={draftType}
                onChange={e => setDraftType(e.target.value)}
              >
                <option value="auction">Auction</option>
                <option value="snake">Snake</option>
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                Paste draft results from ESPN
              </label>
              <textarea
                className={styles.textarea}
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                placeholder="Copy all draft results from ESPN and paste here. You can include multiple teams."
                rows={12}
                disabled={parsing}
              />
            </div>

            {parsing && (
              <div className={styles.parsingIndicator}>
                <span className={styles.parsingSpinner} aria-hidden="true" />
                <div>
                  <strong>Parsing draft results…</strong>
                  <p className={styles.parsingSubtext}>
                    Large leagues (10–14 teams) can take up to a minute. Please wait — this is expected.
                  </p>
                </div>
              </div>
            )}

            {parseError && <p className={styles.error}>{parseError}</p>}

            <div className={styles.buttonRow}>
              <button
                className={styles.secondaryButton}
                onClick={() => setStep('idle')}
                disabled={parsing}
              >
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleParse}
                disabled={parsing || !pasteText.trim()}
              >
                {parsing ? 'Parsing…' : 'Parse Results'}
              </button>
            </div>
          </div>
        )}

        {/* ── Review step ── */}
        {step === 'review' && (
          <div className={styles.importBox}>
            <h4 className={styles.importTitle}>
              Review Parsed Results — {parsedPicks.length} picks found
            </h4>

            {/* Missing playerName warning — inline fix required before import unlocks */}
            {picksWithMissingName.length > 0 && (
              <p className={styles.mappingWarning}>
                {picksWithMissingName.length} pick(s) are missing a player name (highlighted below).
                Enter the player name directly in the highlighted row(s) to unlock import.
              </p>
            )}

            {/* Team mapping */}
            <div className={styles.mappingSection}>
              {Object.keys(teamMappings).length === 0 ? (
                <p className={styles.mappingWarning}>
                  No team names were detected in the pasted data — mapping isn't possible until this is fixed.
                  Go back and check that the pasted text includes team name headers above each set of picks.
                </p>
              ) : (
                <p className={styles.mappingLabel}>
                  Map each ESPN team name to a league member:
                </p>
              )}
              {Object.keys(teamMappings).map(espnName => (
                <div key={espnName} className={styles.mappingRow}>
                  <span className={styles.espnTeamName}>{espnName}</span>
                  <span className={styles.mappingArrow}>→</span>
                  <select
                    className={styles.select}
                    value={teamMappings[espnName]?.memberId || ''}
                    onChange={e => handleMappingChange(espnName, e.target.value)}
                  >
                    <option value="">Select member…</option>
                    {leagueMembers.map(m => (
                      <option key={m.userId || m._id} value={m.userId || m._id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {/* Bulk assign — only shown when there are unassigned per-pick overrides */}
            {Object.values(pickOverrides).some(m => !m.memberId) && (
              <div className={styles.bulkAssignRow}>
                <span className={styles.bulkAssignLabel}>
                  Assign all unassigned picks to:
                </span>
                <select
                  className={styles.select}
                  value={bulkAssignMemberId}
                  onChange={e => setBulkAssignMemberId(e.target.value)}
                >
                  <option value="">Select member…</option>
                  {leagueMembers.map(m => (
                    <option key={m.userId || m._id} value={m.userId || m._id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <button
                  className={styles.secondaryButton}
                  onClick={handleBulkAssign}
                  disabled={!bulkAssignMemberId}
                >
                  Apply
                </button>
              </div>
            )}

            {/* Parsed picks preview */}
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Pos</th>
                    <th>NFL Team</th>
                    <th>ESPN Team</th>
                    {draftType === 'auction' ? <th>Price</th> : <><th>Rd</th><th>Pick</th></>}
                    <th>K</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedPicks.map((p, i) => (
                    <tr key={i} className={(!p.teamName || (!p.playerName && !nameOverrides[i]?.trim())) ? styles.missingTeamRow : undefined}>
                      <td>
                        {p.playerName ? p.playerName : (
                          <input
                            className={styles.inlineInput}
                            type="text"
                            placeholder="Enter player name…"
                            value={nameOverrides[i] || ''}
                            onChange={e => setNameOverrides(prev => ({ ...prev, [i]: e.target.value }))}
                          />
                        )}
                      </td>
                      <td>{p.position}</td>
                      <td>{p.nflTeam}</td>
                      <td>
                        {p.teamName ? p.teamName : (
                          <select
                            className={styles.select}
                            value={pickOverrides[i]?.memberId || ''}
                            onChange={e => handlePickOverrideChange(i, e.target.value)}
                          >
                            <option value="">Assign member…</option>
                            {leagueMembers.map(m => (
                              <option key={m.userId || m._id} value={m.userId || m._id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      {draftType === 'auction'
                        ? <td>${p.auctionPrice ?? '–'}</td>
                        : <><td>{p.round ?? '–'}</td><td>{p.pickNumber ?? '–'}</td></>
                      }
                      <td>{p.keeper ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.buttonRow}>
              <button className={styles.secondaryButton} onClick={() => setStep('paste')}>
                Back
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleImport}
                disabled={importing || !canImport()}
              >
                {importing ? 'Saving…' : 'Confirm & Import'}
              </button>
            </div>
          </div>
        )}

        {/* ── Idle: no draft yet ── */}
        {step === 'idle' && !draft && (
          <div className={styles.emptyState}>
            <p>No draft results for {selectedSeason}.</p>
            {isCommissioner && (
              <button className={styles.primaryButton} onClick={handleStartImport}>
                Import Draft Results
              </button>
            )}
          </div>
        )}

        {/* ── Draft table ── */}
        {step === 'idle' && draft && (
          <>
            <div className={styles.tableControls}>
              <div className={styles.draftMeta}>
                <span className={styles.draftBadge}>{draft.draftType}</span>
                <span className={styles.pickCount}>{picks.length} picks</span>
              </div>
              <div className={styles.filterRow}>
                <select
                  className={styles.positionFilter}
                  value={filterPosition}
                  onChange={e => setFilterPosition(e.target.value)}
                >
                  {uniquePositions.map(p => (
                    <option key={p} value={p}>{p === 'ALL' ? 'All positions' : p}</option>
                  ))}
                </select>
                {isCommissioner && (
                  <button className={styles.secondaryButton} onClick={handleStartImport}>
                    Re-import
                  </button>
                )}
              </div>
            </div>

            {showSortHint && (
              <div className={styles.sortHint}>
                <span>Tip: hold <kbd className={styles.kbd}>Shift</kbd> and click a column to add a secondary sort.</span>
                <button className={styles.sortHintDismiss} onClick={dismissSortHint} aria-label="Dismiss">✕</button>
              </div>
            )}

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.sortable} onClick={e => handleSort('playerName', e)}>
                      Player{sortIcon('playerName')}
                    </th>
                    <th className={styles.sortable} onClick={e => handleSort('position', e)}>
                      Pos{sortIcon('position')}
                    </th>
                    <th className={styles.sortable} onClick={e => handleSort('nflTeam', e)}>
                      NFL Team{sortIcon('nflTeam')}
                    </th>
                    <th className={styles.sortable} onClick={e => handleSort('memberName', e)}>
                      Manager{sortIcon('memberName')}
                    </th>
                    {draft.draftType === 'auction' ? (
                      <th className={styles.sortable} onClick={e => handleSort('auctionPrice', e)}>
                        Price{sortIcon('auctionPrice')}
                      </th>
                    ) : (
                      <>
                        <th className={styles.sortable} onClick={e => handleSort('round', e)}>
                          Rd{sortIcon('round')}
                        </th>
                        <th className={styles.sortable} onClick={e => handleSort('pickNumber', e)}>
                          Pick{sortIcon('pickNumber')}
                        </th>
                      </>
                    )}
                    <th>K</th>
                    {isCommissioner && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {displayedPicks.map(pick => (
                    <tr key={pick._id}>
                      {editingPickId === pick._id ? (
                        <>
                          <td>
                            <input
                              className={styles.inlineInput}
                              value={editForm.playerName}
                              onChange={e => setEditForm(f => ({ ...f, playerName: e.target.value }))}
                            />
                          </td>
                          <td>
                            <input
                              className={styles.inlineInput}
                              value={editForm.position}
                              onChange={e => setEditForm(f => ({ ...f, position: e.target.value }))}
                            />
                          </td>
                          <td>
                            <input
                              className={styles.inlineInput}
                              value={editForm.nflTeam}
                              onChange={e => setEditForm(f => ({ ...f, nflTeam: e.target.value }))}
                            />
                          </td>
                          <td>
                            <select
                              className={styles.inlineInput}
                              value={editForm.memberId || ''}
                              onChange={e => {
                                const member = leagueMembers.find(m => (m.userId || m._id) === e.target.value);
                                setEditForm(f => ({
                                  ...f,
                                  memberId: e.target.value,
                                  memberName: member?.name || f.memberName,
                                }));
                              }}
                            >
                              <option value="">Unknown</option>
                              {leagueMembers.map(m => (
                                <option key={m.userId || m._id} value={m.userId || m._id}>
                                  {m.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          {draft.draftType === 'auction' ? (
                            <td>
                              <input
                                className={styles.inlineInput}
                                type="number"
                                value={editForm.auctionPrice}
                                onChange={e => setEditForm(f => ({ ...f, auctionPrice: e.target.value }))}
                              />
                            </td>
                          ) : (
                            <>
                              <td>
                                <input
                                  className={styles.inlineInput}
                                  type="number"
                                  value={editForm.round}
                                  onChange={e => setEditForm(f => ({ ...f, round: e.target.value }))}
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.inlineInput}
                                  type="number"
                                  value={editForm.pickNumber}
                                  onChange={e => setEditForm(f => ({ ...f, pickNumber: e.target.value }))}
                                />
                              </td>
                            </>
                          )}
                          <td>
                            <input
                              type="checkbox"
                              checked={editForm.keeper}
                              onChange={e => setEditForm(f => ({ ...f, keeper: e.target.checked }))}
                            />
                          </td>
                          <td className={styles.editActions}>
                            <button className={styles.saveInlineButton} onClick={handleSavePick}>Save</button>
                            <button className={styles.cancelInlineButton} onClick={() => setEditingPickId(null)}>✕</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{pick.playerName}</td>
                          <td className={styles.positionCell}>{pick.position}</td>
                          <td>{pick.nflTeam}</td>
                          <td>{pick.memberName}</td>
                          {draft.draftType === 'auction' ? (
                            <td className={styles.priceCell}>
                              {pick.auctionPrice != null ? `$${pick.auctionPrice}` : '–'}
                            </td>
                          ) : (
                            <>
                              <td>{pick.round ?? '–'}</td>
                              <td>{pick.pickNumber ?? '–'}</td>
                            </>
                          )}
                          <td>{pick.keeper ? '✓' : ''}</td>
                          {isCommissioner && (
                            <td>
                              <button
                                className={styles.editButton}
                                onClick={() => handleEditPick(pick)}
                              >
                                Edit
                              </button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DraftResults;
